import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

const LOGO_URL = "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6992dc80a_WeSonder1024x1024.png";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const createPageUrl = (pageName: string) => {
    // Map page names to their correct URL paths
    const pagePaths: { [key: string]: string } = {
      'Downloads': '/downloads',
      'ForMyClients': '/for-my-clients',
      'Home': '/home'
    };
    
    return pagePaths[pageName] || `/${pageName.toLowerCase()}`;
  };

  const isActivePage = (pageName: string) => {
    return location.pathname === createPageUrl(pageName);
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="bg-white shadow-sm border-b border-slate-200 fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Navigation */}
            <div className="flex items-center space-x-8">
              <Link to="/" className="flex items-center space-x-3">
                <img src={LOGO_URL} alt="WeSonder Logo" className="h-8 w-auto" />
                <span className="text-xl font-bold text-slate-900">WeSonder</span>
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex space-x-8">
                <Link 
                  to={createPageUrl("Downloads")} 
                  className={`text-sm font-medium transition-colors ${
                    isActivePage("Downloads") 
                      ? 'text-blue-600' 
                      : 'text-slate-700 hover:text-slate-900'
                  }`}
                >
                  For Me
                </Link>
                <Link 
                  to={createPageUrl("ForMyClients")} 
                  className={`text-sm font-medium transition-colors ${
                    isActivePage("ForMyClients") 
                      ? 'text-blue-600' 
                      : 'text-slate-700 hover:text-slate-900'
                  }`}
                >
                  For My Clients
                </Link>
                <Link 
                  to="/blog" 
                  className={`text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/blog')
                      ? 'text-blue-600'
                      : 'text-slate-700 hover:text-slate-900'
                  }`}
                >
                  Articles
                </Link>
              </div>
            </div>
            
            {/* CTA Button */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to={createPageUrl("Downloads")}>
                <Button 
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 text-sm font-medium"
                >
                  Get the App
                </Button>
              </Link>
            </div>
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-slate-700 hover:text-slate-900 p-2"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link to={createPageUrl("Downloads")} className="text-slate-700 hover:text-slate-900 block px-3 py-2 text-base font-medium">
                For Me
              </Link>
              <Link to={createPageUrl("ForMyClients")} className="text-slate-700 hover:text-slate-900 block px-3 py-2 text-base font-medium">
                For My Clients
              </Link>
              <Link to="/blog" className="text-slate-700 hover:text-slate-900 block px-3 py-2 text-base font-medium">
                Articles
              </Link>
              <div className="px-3 py-2">
                <Link to={createPageUrl("Downloads")}>
                  <Button 
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    Get the App
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
      
      {/* Content */}
      <main className="pt-16">
        {children}
      </main>
      
      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                 <img src={LOGO_URL} alt="WeSonder Logo" className="h-10 w-auto" />
                 <h3 className="text-2xl font-bold">WeSonder</h3>
              </div>
              <p className="text-slate-400 body-text">
                Understand your personality and relationships through AI-powered analysis of your text messages.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to={createPageUrl("Downloads")} className="hover:text-white transition-colors">For Me</Link></li>
                <li><Link to={createPageUrl("ForMyClients")} className="hover:text-white transition-colors">For My Clients</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Articles</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="/help" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="https://drive.google.com/file/d/1ghiQMwQ93za4rWLnrHG7az4uuaZdx4MN/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Privacy Policy</a></li>
                                  <li><a href="https://drive.google.com/file/d/1c_vlzbOfQohlbJBDsN7t-JD0x5-C3vNk/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="mailto:support@wesonder.io" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2025 WeSonder. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
} 